import { z } from 'zod';
import { TIPOS_SOCIETARIOS } from './vocabulario.js';
/**
 * Os quatro atos que NÃO são o contrato — e os dados que cada um exige.
 *
 * ## Por que eles não cabem no molde da alteração
 * A alteração contratual é o contrato em outro momento: mesmos campos, valores
 * diferentes. Estes quatro são documentos de outra natureza, e cada um pede
 * informação que nenhum outro pede:
 *
 * - **Distrato** encerra a sociedade. Precisa saber quem guarda os livros
 *   pelos cinco anos seguintes e como o patrimônio foi partilhado.
 * - **Procuração** nem é ato societário: é mandato. Não tem sócios
 *   deliberando; tem outorgante, outorgado e poderes.
 * - **Ata** registra uma reunião. Precisa de local, hora, quem compareceu e o
 *   que foi deliberado.
 * - **Transformação** troca o TIPO da sociedade, com quórum próprio.
 *
 * Forçá-los no molde da alteração produziria quatro documentos com campos
 * vazios e nenhum dos campos que eles de fato precisam.
 */
/**
 * DISTRATO — a dissolução e extinção da sociedade.
 *
 * `responsavelLivrosId` não é burocracia do sistema: o art. 1.194 do Código
 * Civil obriga a guarda da escrituração enquanto não prescreverem as ações, e
 * a Junta devolve distrato que não nomeia quem guarda. É o campo que o
 * contador mais esquece, e o que mais volta.
 */
export const distratoSchema = z.object({
    data: z.string().date(),
    /**
     * Motivo da dissolução, como sai no documento — em minúsculas, sem ponto.
     * Ex.: "encerramento das atividades por decisão dos sócios".
     */
    motivo: z.string().min(10),
    /** Quem fica com os livros e documentos. Tem de ser sócio ou administrador. */
    responsavelLivrosId: z.string().uuid(),
    /**
     * O acervo líquido a partilhar, em centavos. Zero é legítimo e comum:
     * sociedade que encerrou sem patrimônio a dividir.
     */
    acervoLiquidoCentavos: z.number().int().nonnegative(),
    /**
     * Se há passivo a descoberto. Muda a redação: com passivo, os sócios
     * declaram assumi-lo na proporção das quotas, e é o que a Junta procura.
     */
    temPassivo: z.boolean().default(false),
});
export const PODERES_PROCURACAO = [
    'AD_JUDICIA',
    'ADMINISTRATIVOS',
    'BANCARIOS',
    'TRIBUTARIOS',
    'TRABALHISTAS',
    'REPRESENTACAO_JUNTA',
];
export const NOME_PODER_PROCURACAO = {
    AD_JUDICIA: 'representar em juízo, com os poderes da cláusula ad judicia',
    ADMINISTRATIVOS: 'praticar atos de administração perante órgãos públicos',
    BANCARIOS: 'movimentar contas bancárias, emitir e endossar cheques',
    TRIBUTARIOS: 'representar perante as fazendas federal, estadual e municipal',
    TRABALHISTAS: 'representar perante a Justiça do Trabalho e sindicatos',
    REPRESENTACAO_JUNTA: 'representar perante a Junta Comercial e órgãos de registro',
};
/**
 * PROCURAÇÃO — mandato outorgado pela sociedade.
 *
 * O outorgado NÃO precisa ser sócio nem administrador: procuração para
 * advogado ou despachante é o caso mais comum. Ele precisa estar no cadastro
 * de pessoas, porque o documento o qualifica por inteiro.
 */
export const procuracaoSchema = z.object({
    data: z.string().date(),
    outorgadoId: z.string().uuid(),
    poderes: z.array(z.enum(PODERES_PROCURACAO)).min(1),
    /**
     * `null` = por prazo indeterminado, até revogação.
     *
     * Procuração sem prazo é válida, mas a de prazo certo é o que se usa para
     * um ato específico — e datar é o que evita um mandato esquecido valendo
     * cinco anos depois.
     */
    validaAte: z.string().date().nullable().default(null),
    /** Se o procurador pode substabelecer. Muda a cláusula final. */
    podeSubstabelecer: z.boolean().default(false),
    /** Finalidade, quando há uma específica. Entra na cláusula de poderes. */
    finalidade: z.string().optional(),
});
/**
 * ATA — o registro de uma reunião ou assembleia de sócios.
 *
 * O que a torna válida perante a Junta é o QUÓRUM: quem compareceu, e se o
 * total de quotas presentes atinge o mínimo que a lei ou o contrato exigem
 * para a matéria deliberada. Uma ata sem a lista de presentes é papel.
 */
export const ataSchema = z.object({
    data: z.string().date(),
    /** Hora de abertura, `HH:MM`. A Junta lê, e a ata sem hora volta. */
    hora: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM'),
    local: z.string().min(3),
    /** Ids dos sócios PRESENTES. É deles que sai o quórum. */
    presentesIds: z.array(z.string().uuid()).min(1),
    /** A pauta, uma linha por item. */
    ordemDoDia: z.array(z.string().min(5)).min(1),
    /** O que foi decidido, na ordem da pauta. */
    deliberacoes: z.array(z.string().min(5)).min(1),
    /** Quem presidiu e quem secretariou — a ata exige as duas assinaturas. */
    presidenteId: z.string().uuid(),
    secretarioId: z.string().uuid(),
});
/**
 * TRANSFORMAÇÃO — a sociedade muda de TIPO sem se dissolver (art. 1.113 CC).
 *
 * ## O que ela exige, e por que o consentimento é unânime
 * O art. 1.114 do Código Civil pede o consentimento de TODOS os sócios, salvo
 * se o contrato já previr a transformação — e nesse caso quem discordar pode
 * se retirar. É a diferença de responsabilidade que justifica: transformar uma
 * limitada em nome coletivo torna ilimitada a responsabilidade de quem antes
 * respondia até o capital.
 *
 * ## O que este sistema NÃO transforma
 * Para `SA` e `CA` a transformação exige ESTATUTO e capital em AÇÕES, que o
 * gerador não monta. Oferecer o destino sem saber redigir o resultado
 * produziria um instrumento de transformação que aponta para um documento que
 * nunca sai.
 */
export const transformacaoSchema = z.object({
    data: z.string().date(),
    tipoNovo: z.enum(TIPOS_SOCIETARIOS),
    /**
     * Todos os sócios consentiram? O art. 1.114 exige, e o schema não deixa
     * passar `false` — a transformação sem unanimidade tem outro rito (direito
     * de retirada), que este produto ainda não trata.
     */
    consentimentoUnanime: z.literal(true),
    /** Motivo, como sai no documento. */
    motivo: z.string().min(10),
});
/**
 * Os tipos para os quais este sistema sabe transformar.
 *
 * Os mesmos cinco que o gerador monta como contrato — ver
 * `TIPOS_COM_CONTRATO` no front. `SA` e `CA` ficam de fora até a fatia de
 * ações e estatuto.
 */
export const DESTINOS_DE_TRANSFORMACAO = ['SLU', 'LTDA', 'SS', 'NC', 'CS'];
//# sourceMappingURL=atos-especiais.js.map